// React core
import { useRef, useState } from "react";

// Data fetching
import { useMutation, useQuery } from "@tanstack/react-query";

// Animation
import { motion, AnimatePresence } from "framer-motion";

// Icons
import { Loader2, Wallet, ShieldCheck } from "lucide-react";

// Toast notifications
import { toast } from "sonner";

// API & shared components
import {
  createMerchantDeposit,
  getProfile,
} from "@/shared/api/services/merchantService";
import { Button } from "@/shared/components/ui/button";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

// Logos and Sections
import { PAYMENT_METHODS } from "@/shared/constants/paymentMethods";
import MethodAmountSection from "@/shared/components/merchant/deposit/StepMethodAmount";
import CustomerDetailsSection from "@/shared/components/merchant/deposit/StepCustomerDetails";
import BillingAddressSection from "@/shared/components/merchant/deposit/StepBillingAddress";

// ─── Constants ────────────────────────────────────────────────────────────────

const VALIDATED_FIELDS = [
  "platformName",
  "amount",
  "firstname",
  "lastname",
  "email",
  "phone",
  "city",
  "address",
  "state",
  "postcode",
];

const INITIAL_FORM = {
  platformName: "",
  amount: "",
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  city: "",
  address: "",
  state: "",
  postcode: "",
};

// ─── Validation ───────────────────────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^03\d{9}$/;

function lengthError(value, min, max) {
  if (value.length < min) return `Min ${min} characters`;
  if (value.length > max) return `Max ${max} characters`;
  return "";
}

const FIELD_VALIDATORS = {
  platformName: (form) => (form.platformName ? "" : "Select a platform"),
  amount: (form, symbol) => {
    const min =
      PAYMENT_METHODS.find((m) => m.id === form.platformName)?.minAmount ?? 100;
    const val = Number(form.amount);
    return !form.amount || isNaN(val) || val < min
      ? `Minimum deposit is ${symbol} ${min}`
      : "";
  },
  firstname: (form) => lengthError(form.firstname, 2, 100),
  lastname: (form) => lengthError(form.lastname, 2, 100),
  city: (form) => lengthError(form.city, 2, 100),
  state: (form) => lengthError(form.state, 2, 100),
  address: (form) => lengthError(form.address, 5, 200),
  email: (form) => {
    if (!form.email) return "Email is required";
    return EMAIL_REGEX.test(form.email) ? "" : "Valid email required";
  },
  phone: (form) => (PHONE_REGEX.test(form.phone) ? "" : "Format: 03XXXXXXXXX"),
  postcode: (form) =>
    /^[0-9]{3,20}$/.test(form.postcode) ? "" : "3–20 digits required",
};

function getFieldError(field, form, symbol) {
  return FIELD_VALIDATORS[field]?.(form, symbol) ?? "";
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function MerchantDeposit() {
  const { t } = useLanguage();

  const { data: profile } = useQuery({
    queryKey: ["merchant-profile"],
    queryFn: getProfile,
  });

  const currency = String(profile?.currency || "PKR").toUpperCase();
  const currencySymbol = currency === "PKR" ? "Rs" : "฿";

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const isSubmittingRef = useRef(false);
  const fieldRefs = useRef({});

  const { mutate: submitDeposit, isPending: loading } = useMutation({
    mutationFn: createMerchantDeposit,
    onSuccess: ({ checkout_url }) => {
      if (checkout_url) {
        window.location.href = checkout_url;
      } else {
        isSubmittingRef.current = false;
      }
    },
    onError: (err) => {
      isSubmittingRef.current = false;
      toast.error(err.message || "Deposit request failed");
    },
  });

  const normalizeForm = () =>
    Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, String(v).trim()]),
    );

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateField = (field, value) => {
    const snapshot = { ...normalizeForm(), [field]: String(value).trim() };
    const message = getFieldError(field, snapshot, currencySymbol);
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
    return !message;
  };

  const validateAll = () => {
    const normalized = normalizeForm();
    const errs = {};
    VALIDATED_FIELDS.forEach((field) => {
      const msg = getFieldError(field, normalized, currencySymbol);
      if (msg) errs[field] = msg;
    });

    setErrors(errs);

    const firstError = VALIDATED_FIELDS.find((f) => errs[f]);
    if (firstError && fieldRefs.current[firstError]) {
      fieldRefs.current[firstError].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmittingRef.current || loading) return;
    if (!validateAll()) return;
    isSubmittingRef.current = true;
    submitDeposit(normalizeForm());
  };

  const isDisabled = loading || isSubmittingRef.current;

  return (
    <MerchantLayout currentPage="MerchantDeposit">
      <div className="max-w-2xl mx-auto px-3 sm:px-4 lg:px-0 mb-10">
        <AnimatePresence mode="wait">
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-slate-900">
                {t("deposits") || "Deposit"} — JazzCash / EasyPaisa
              </h1>
              <p className="text-slate-500">
                Initiate a mobile wallet deposit for your customer
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <MethodAmountSection
                form={form}
                updateField={updateField}
                fieldRefs={fieldRefs}
                errors={errors}
                currency={currency}
                currencySymbol={currencySymbol}
              />

              <CustomerDetailsSection
                form={form}
                updateField={updateField}
                validateField={validateField}
                errors={errors}
                fieldRefs={fieldRefs}
              />

              <BillingAddressSection
                form={form}
                updateField={updateField}
                validateField={validateField}
                errors={errors}
                fieldRefs={fieldRefs}
              />

              <div className="space-y-4">
                <Button
                  type="submit"
                  disabled={isDisabled}
                  className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700 gap-2 shadow-lg shadow-blue-100 disabled:opacity-60"
                >
                  {isDisabled ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Wallet className="h-5 w-5" />
                      Initiate Deposit
                      {parseFloat(form.amount) > 0 && (
                        <span className="ml-1 font-bold">
                          — {currencySymbol}{" "}
                          {parseFloat(form.amount).toLocaleString()}
                        </span>
                      )}
                    </>
                  )}
                </Button>

                <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <ShieldCheck className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    {[
                      "Select JazzCash or EasyPaisa and fill details",
                      "Redirect customer to hosted checkout page",
                      "Customer completes payment via mobile app",
                      "Receive instant callback upon success",
                    ].map((step, i) => (
                      <div key={i} className="text-xs text-blue-800 flex gap-2">
                        <span className="font-semibold text-blue-900">
                          {i + 1}.
                        </span>
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </form>
          </motion.div>
        </AnimatePresence>
      </div>
    </MerchantLayout>
  );
}
